/**
 * The one level picker: the assembler's view widget switches the level on
 * screen with it, and the Details panels' Level row moves a room with it, in
 * the room editor and for a room selected in the assembler.
 */

import { css } from '@emotion/react';
import { levelLabel } from '@kaiizen/kai-floor-plan-editor-core';
import { fieldWidth, focusRing, ink, monoLabel, paper, white } from '../../styles';
import { FIELD_ROW_STYLE } from './chrome';
import { ReadOnlyField } from './fields';
import { usePanelReadOnly } from './panelReadOnly';

/**
 * The editor's action-button language: mono, ink hairline, white; a solid
 * caret says it opens. Hosts commonly pin every form control to 16px with
 * !important to stop iOS Safari zooming on focus; the picker's own type has
 * to win that, so its size carries the same weight.
 */
const LEVEL_SELECT_STYLE = css`
  label: LEVEL_SELECT_STYLE;
  && {
    ${monoLabel}
    appearance: none; -webkit-appearance: none;
    box-sizing: border-box; width: auto; max-width: none; height: auto; min-height: 0; margin: 0;
    padding: 6px 28px 6px 12px; border: 1px solid ${ink}; border-radius: 0;
    font-size: 10px !important; letter-spacing: 0.06em; line-height: 1.5;
    background: ${white} url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><polygon points='0,0 10,0 5,6' fill='%231a1a1a'/></svg>") no-repeat right 10px center;
    color: ${ink}; cursor: pointer;
    &:hover { background-color: ${paper}; }
    &:focus { outline: none; box-shadow: ${focusRing}; }
  }
`;

/** In a panel row the picker is as wide as the row's other fields. */
const FIELD_LEVEL_SELECT_STYLE = css`
  label: FIELD_LEVEL_SELECT_STYLE;
  && { width: ${fieldWidth}; }
`;

export interface LevelSelectProps {
  value: number;
  /** The levels to choose from, ascending. */
  levels: readonly number[];
  onChange: (level: number) => void;
}

/** The select itself; the `css` its callers give arrives as `className`. */
function LevelPicker(
  { value, levels, onChange, ...select }: LevelSelectProps & { id?: string; 'data-testid'?: string; className?: string },
): React.JSX.Element {
  return (
    <select {...select} value={value} onChange={e => onChange(Number(e.currentTarget.value))}>
      {levels.map(level => <option key={level} value={level}>{levelLabel(level)}</option>)}
    </select>
  );
}

/** The view widget's level switcher. */
export function LevelSelect(props: LevelSelectProps): React.JSX.Element {
  return <LevelPicker {...props} css={LEVEL_SELECT_STYLE} data-testid='assembly-level' />;
}

/** A Details panel's Level row: the room's level, picked with the view widget's own control, or just its name in a read-only panel. */
export function LevelField({ id, ...props }: LevelSelectProps & { id: string }): React.JSX.Element {
  if (usePanelReadOnly()) return <ReadOnlyField id={id} label='Level' value={levelLabel(props.value)} />;
  return (
    <div css={FIELD_ROW_STYLE}>
      <label htmlFor={id}>Level</label>
      <LevelPicker {...props} id={id} css={[LEVEL_SELECT_STYLE, FIELD_LEVEL_SELECT_STYLE]} />
    </div>
  );
}
